import { computeArrivalTicks, frameRouteFingerprint, sha256Hex } from "./fingerprint.ts";
import { findStablePath } from "./stable-a-star.ts";
import type {
  NavLayoutContract,
  NavSnapshot,
  ReadonlyPointXZ,
  RouteGraphEdge,
  RouteHandle,
  RouteIntent,
  RouteQuery,
  StaticNavWorld,
} from "./types.ts";
import { validateNavLayout } from "./validation.ts";

const NONE: RouteIntent = Object.freeze({ kind: "none" });

function freezePoints(points: readonly ReadonlyPointXZ[]): readonly ReadonlyPointXZ[] {
  return Object.freeze(points.map((point) => Object.freeze([point[0], point[1]] as const)));
}

function buildWaypoints(
  snapshot: Readonly<NavSnapshot>,
  nodeIds: readonly string[],
  edgeIds: readonly string[],
): readonly ReadonlyPointXZ[] | null {
  const points: ReadonlyPointXZ[] = [];
  for (let index = 0; index < edgeIds.length; index += 1) {
    const edge = snapshot.edges.find((candidate) => candidate.id === edgeIds[index]);
    /* v8 ignore next -- validated A* edge ids always resolve in the immutable snapshot. */
    if (edge === undefined) return null;
    const fromNode = nodeIds[index]!;
    const oriented = edge.from === fromNode ? edge.polyline : [...edge.polyline].reverse();
    for (let pointIndex = 0; pointIndex < oriented.length; pointIndex += 1) {
      if (index > 0 && pointIndex === 0) continue;
      points.push(oriented[pointIndex]!);
    }
  }
  return freezePoints(points);
}

function validTick(value: number): boolean {
  return Number.isSafeInteger(value) && value >= 0;
}

export function createStaticNavWorld(layout: NavLayoutContract): StaticNavWorld {
  const snapshot = validateNavLayout(layout);
  const nodeIds = snapshot.nodes.map((node) => node.id);
  const handles = new Map<string, Readonly<RouteHandle>>();
  const canceled = new Set<string>();
  let nextHandleId = 1;

  const isKnown = (handle: Readonly<RouteHandle>): boolean =>
    handle.revision === snapshot.revision && handles.get(handle.handleId) === handle;

  return Object.freeze({
    get snapshot(): Readonly<NavSnapshot> {
      return snapshot;
    },
    queryRoute(query: Readonly<RouteQuery>): Readonly<RouteHandle> | null {
      const profile = snapshot.arrivalProfiles[query.profileId];
      if (
        profile === undefined ||
        !validTick(query.departureTick) ||
        query.fromNodeId === query.toNodeId ||
        !nodeIds.includes(query.fromNodeId) ||
        !nodeIds.includes(query.toNodeId)
      ) return null;
      const path = findStablePath(snapshot.nodes.map((node) => node.id), snapshot.edges as readonly RouteGraphEdge[], query.fromNodeId, query.toNodeId);
      /* v8 ignore next -- validation requires a connected graph and query ids are checked above. */
      if (path === null) return null;
      const waypoints = buildWaypoints(snapshot, path.nodeIds, path.edgeIds);
      /* v8 ignore next -- a validated path contains only snapshot edge ids. */
      if (waypoints === null) return null;
      const arrivalTicks = computeArrivalTicks(path.totalCostMilliWU, profile);
      if (query.departureTick > Number.MAX_SAFE_INTEGER - arrivalTicks) return null;
      const fingerprint = sha256Hex(frameRouteFingerprint({
        revision: snapshot.revision,
        fromNodeId: query.fromNodeId,
        toNodeId: query.toNodeId,
        totalCostMilliWU: path.totalCostMilliWU,
        profileId: query.profileId,
        arrivalTicks,
        waypoints,
      }));
      const handleId = `route-${nextHandleId.toString(10)}`;
      nextHandleId += 1;
      const handle: Readonly<RouteHandle> = Object.freeze({
        handleId,
        revision: snapshot.revision,
        fromNodeId: query.fromNodeId,
        toNodeId: query.toNodeId,
        profileId: query.profileId,
        departureTick: query.departureTick,
        arrivalEligibleTick: query.departureTick + arrivalTicks,
        nodeIds: path.nodeIds,
        edgeIds: path.edgeIds,
        totalCostMilliWU: path.totalCostMilliWU,
        waypoints,
        fingerprint,
      });
      handles.set(handleId, handle);
      return handle;
    },
    cancel(handle: Readonly<RouteHandle>): boolean {
      if (!isKnown(handle)) return false;
      canceled.add(handle.handleId);
      return true;
    },
    readIntent(handle: Readonly<RouteHandle>, authorityTick: number, actorPoint: ReadonlyPointXZ): RouteIntent {
      if (!isKnown(handle) || canceled.has(handle.handleId) || !validTick(authorityTick)) return NONE;
      const endpoint = handle.waypoints[handle.waypoints.length - 1];
      /* v8 ignore next -- known route handles always contain at least one validated edge. */
      if (endpoint === undefined || !Number.isSafeInteger(actorPoint[0]) || !Number.isSafeInteger(actorPoint[1])) return NONE;
      const dx = actorPoint[0] - endpoint[0];
      const dz = actorPoint[1] - endpoint[1];
      if (authorityTick >= handle.arrivalEligibleTick && dx * dx + dz * dz <= snapshot.endpointToleranceMilliWU ** 2) {
        return Object.freeze({ kind: "arrival", nodeId: handle.toNodeId });
      }
      return Object.freeze({ kind: "movement", waypoints: handle.waypoints });
    },
  });
}
